import { useEffect, useRef, useState } from "react";
import jsQR from "jsqr";
import { X, Camera, AlertTriangle, Loader2 } from "lucide-react";

/** Scanner QR caméra (BarcodeDetector natif si dispo, sinon jsQR) — fonctionne hors ligne. */
export function QrScanner({ onResult, onClose, hint }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [error, setError] = useState(null);
  const [ready, setReady] = useState(false);
  const [manual, setManual] = useState("");

  useEffect(() => {
    let stream; let raf; let stopped = false;
    const detector = typeof window !== "undefined" && "BarcodeDetector" in window ? new window.BarcodeDetector({ formats: ["qr_code"] }) : null;
    const tick = async () => {
      if (stopped) return;
      const video = videoRef.current;
      if (video && video.readyState >= 2) {
        try {
          let text = null;
          if (detector) {
            const codes = await detector.detect(video);
            text = codes[0]?.rawValue || null;
          } else {
            const canvas = canvasRef.current;
            const w = video.videoWidth, h = video.videoHeight;
            if (w && h) {
              canvas.width = w; canvas.height = h;
              const ctx = canvas.getContext("2d", { willReadFrequently: true });
              ctx.drawImage(video, 0, 0, w, h);
              const img = ctx.getImageData(0, 0, w, h);
              text = jsQR(img.data, w, h, { inversionAttempts: "dontInvert" })?.data || null;
            }
          }
          if (text) { stopped = true; onResult(text); return; }
        } catch { /* frame ignorée */ }
      }
      raf = setTimeout(tick, detector ? 150 : 250);
    };
    navigator.mediaDevices?.getUserMedia({ video: { facingMode: { ideal: "environment" } }, audio: false })
      .then((s) => {
        stream = s;
        if (videoRef.current) { videoRef.current.srcObject = s; videoRef.current.play().catch(() => {}); }
        setReady(true);
        tick();
      })
      .catch((e) => setError(e?.name === "NotAllowedError" ? "Accès à la caméra refusé. Autorisez la caméra ou saisissez le code manuellement." : "Caméra indisponible sur cet appareil."));
    return () => { stopped = true; clearTimeout(raf); stream?.getTracks().forEach((t) => t.stop()); };
  }, [onResult]);

  return (
    <div className="fixed inset-0 z-[80] bg-black/95 flex flex-col" data-testid="qr-scanner">
      <div className="flex items-center justify-between px-4 py-3 text-white">
        <span className="text-sm font-bold flex items-center gap-2"><Camera className="w-4 h-4 text-[#f7c935]" /> Scanner le QR du poste</span>
        <button onClick={onClose} className="p-1 rounded-full bg-white/10" data-testid="qr-scanner-close"><X className="w-5 h-5" /></button>
      </div>
      <div className="relative flex-1 flex items-center justify-center overflow-hidden">
        <video ref={videoRef} className="absolute inset-0 w-full h-full object-cover" muted playsInline />
        <canvas ref={canvasRef} className="hidden" />
        {!ready && !error && <Loader2 className="w-8 h-8 animate-spin text-[#f7c935] relative" />}
        {ready && <div className="relative w-60 h-60 border-2 border-[#f7c935] rounded-2xl shadow-[0_0_0_9999px_rgba(0,0,0,0.45)]" />}
        {error && (
          <div className="relative mx-6 p-4 rounded-xl bg-rose-500/15 border border-rose-500/30 text-rose-200 text-xs flex gap-2" data-testid="qr-scanner-error">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" /> {error}
          </div>
        )}
      </div>
      <div className="p-4 space-y-2 text-white">
        {hint && <p className="text-[11px] text-slate-400">{hint}</p>}
        <form onSubmit={(e) => { e.preventDefault(); if (manual.trim()) onResult(manual.trim()); }} className="flex gap-2">
          <input value={manual} onChange={(e) => setManual(e.target.value)} placeholder="Ou collez le lien / code du QR" className="flex-1 bg-white/10 rounded-lg px-3 py-2 text-xs outline-none" data-testid="qr-manual-input" />
          <button type="submit" className="px-3 py-2 rounded-lg bg-[#f7c935] text-[#0b0811] text-xs font-bold" data-testid="qr-manual-submit">OK</button>
        </form>
      </div>
    </div>
  );
}

/** Extrait {terminal_id, qr_token} d'un lien QR de poste (…/punch?t=TOKEN&term=ID) ou d'un texte "ID:TOKEN". */
export function parsePunchQr(text) {
  try {
    const url = new URL(text, window.location.origin);
    const t = url.searchParams.get("t"); const term = url.searchParams.get("term");
    if (t && term) return { terminal_id: term, qr_token: t };
  } catch { /* pas une URL */ }
  const m = /^(term_[a-z0-9]+)[:|](\w+)$/i.exec(text.trim());
  return m ? { terminal_id: m[1], qr_token: m[2] } : null;
}
